import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowsClockwiseBold,
    PiCubeBold,
    PiDownloadSimpleBold,
    PiGridFourBold,
    PiListBold,
    PiMagnifyingGlassBold,
    PiStackBold,
    PiTruckBold,
    PiUploadSimpleBold,
} from 'react-icons/pi';
import { Button, Input, PageHeader, Skeleton, Text } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import TablePagination from '@v2/components/TablePagination';
import api from '@v2/lib/api';
import { isSuperAdmin } from '@v2/lib/auth';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import ProductCatalogSidebar from '@v2/features/products/ProductCatalogSidebar';
import ProductGridCard from '@v2/features/products/ProductGridCard';
import ProductListRow from '@v2/features/products/ProductListRow';
import ProductStockImportModal from '@v2/features/products/ProductStockImportModal';
import type {
    ProductCatalogFilter,
    ProductFilterOptions,
    ProductListResponse,
    SortMode,
    ViewMode,
} from '@v2/features/products/types';
import { DEFAULT_PRODUCT_FILTER } from '@v2/features/products/types';

function ProductGridSkeleton({ count = 10 }: { count?: number }) {
    return (
        <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {Array.from({ length: count }, (_, i) => (
                <div
                    key={i}
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                    <Skeleton className="h-[200px] w-full rounded-none" />
                    <div className="space-y-2 p-3">
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <div className="flex items-center justify-between pt-1">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-5 w-20 rounded-full" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ProductListSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div>
            <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-2 lg:grid lg:grid-cols-[3rem_minmax(0,1.4fr)_7rem_6rem_5.5rem_5.5rem_5.5rem] lg:gap-3 dark:border-gray-700/50 dark:bg-gray-800/40">
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="ms-auto h-3 w-10" />
                <Skeleton className="mx-auto h-3 w-10" />
                <Skeleton className="mx-auto h-3 w-10" />
            </div>
            {Array.from({ length: count }, (_, i) => (
                <div
                    key={i}
                    className="flex w-full items-center gap-4 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-gray-700/50"
                >
                    <Skeleton className="size-12 shrink-0 rounded-lg" />
                    <div className="min-w-0 flex-1 space-y-2">
                        <Skeleton className="h-4 w-2/3 max-w-xs" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="hidden h-5 w-16 rounded-full lg:block" />
                    <Skeleton className="hidden h-4 w-20 md:block" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-5 w-12 rounded-full" />
                    <Skeleton className="h-5 w-12 rounded-full" />
                </div>
            ))}
        </div>
    );
}

function filterToParams(filter: ProductCatalogFilter): Record<string, string> {
    switch (filter.type) {
        case 'all':
            return {};
        case 'in_stock':
            return { stock: 'in_stock' };
        case 'out_of_stock':
            return { stock: 'out_of_stock' };
        case 'in_transit':
            return { stock: 'in_transit' };
        case 'category':
            return { category: filter.value };
        case 'group':
            return { group: filter.value };
        case 'type':
            return { type: filter.value };
        case 'series':
            return { series: filter.value };
    }
}

export default function ProductListPage() {
    const { t } = useI18n();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<ProductCatalogFilter>(DEFAULT_PRODUCT_FILTER);
    const [view, setView] = useState<ViewMode>('grid');
    const [sort, setSort] = useState<SortMode>('name');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [showStockImportModal, setShowStockImportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [pricesLoading, setPricesLoading] = useState(false);
    const canExportPrices = Boolean(window.__V2__?.can_export_prices);
    const queryClient = useQueryClient();
    const canExportProduct = isSuperAdmin();
    const canImportProductStock = Boolean(window.__V2__?.can_import_product_stock);
    const canViewProductStockImportLog = Boolean(window.__V2__?.can_view_product_stock_import_log);

    useEffect(() => {
        setPage(1);
    }, [search, filter, sort, view]);

    const params = useMemo(() => {
        const p: Record<string, string | number> = {
            page,
            per_page: perPage,
            sort,
            dir: sort === 'name' ? 'asc' : 'desc',
            ...filterToParams(filter),
        };
        if (search.trim()) p.search = search.trim();
        return p;
    }, [search, filter, sort, view, page, perPage]);

    const exportParams = useMemo(() => {
        const p: Record<string, string> = { ...filterToParams(filter) };
        if (search.trim()) p.search = search.trim();
        return p;
    }, [search, filter]);

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['products', params],
        queryFn: async () => (await api.get<ProductListResponse>('/products', { params })).data,
    });

    const { data: filterOpts } = useQuery({
        queryKey: ['products-filter-options'],
        queryFn: async () => (await api.get<ProductFilterOptions>('/products/filter-options')).data,
    });

    const syncMutation = useMutation({
        mutationFn: async () => (await api.post<{ message: string }>('/products/sync')).data,
        onSuccess: (r) => {
            setShowSyncModal(false);
            toast.success(r.message);
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['products-filter-options'] });
        },
        onError: () => toast.error('Falha ao iniciar sincronização.'),
    });

    const handleExport = useCallback(async () => {
        setExportLoading(true);
        try {
            const response = await api.get('/products/export', {
                params: exportParams,
                responseType: 'blob',
            });
            const url = URL.createObjectURL(
                new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `estoque-produtos-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast.success('Planilha de estoque gerada.');
        } catch {
            toast.error('Erro ao extrair produtos. Tente novamente.');
        } finally {
            setExportLoading(false);
        }
    }, [exportParams]);

    const handleCatalogExport = useCallback(async () => {
        setCatalogLoading(true);
        try {
            const response = await api.get('/products/export-catalog', { responseType: 'blob' });
            const url = URL.createObjectURL(
                new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `catalogo-produtos-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast.success(t('v2.products.export_catalog_ok'));
        } catch {
            toast.error(t('v2.products.export_catalog_error'));
        } finally {
            setCatalogLoading(false);
        }
    }, [t]);

    // Conferência de preços/custos: preço segue no Ploomes, o Hub só exporta o que sincronizou.
    const handlePricesExport = useCallback(async () => {
        setPricesLoading(true);
        try {
            const response = await api.get('/products/export-prices', { responseType: 'blob' });
            const url = URL.createObjectURL(
                new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `precos-custos-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t('v2.products.export_prices_error'));
        } finally {
            setPricesLoading(false);
        }
    }, [t]);

    const products = data?.data ?? [];
    const meta = data?.meta;
    const stats = filterOpts?.stats;

    return (
        <div className="space-y-6">
            <ConfirmModal
                open={showSyncModal}
                onClose={() => setShowSyncModal(false)}
                onConfirm={() => syncMutation.mutate()}
                title="Sincronizar Ploomes"
                description="Enfileirar sincronização de produtos com o Ploomes? O processamento ocorre em background."
                confirmLabel="Sincronizar"
                cancelLabel="Cancelar"
                loading={syncMutation.isPending}
            />
            <ProductStockImportModal
                open={showStockImportModal}
                onClose={() => setShowStockImportModal(false)}
                canImport={canImportProductStock}
                canViewLog={canViewProductStockImportLog}
            />
            <PageHeader title="Produtos" breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Produtos' }]}>
                <div className="flex items-center gap-2">
                    {(canImportProductStock || canViewProductStockImportLog) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowStockImportModal(true)}
                        >
                            <PiUploadSimpleBold className="me-1.5 size-4" />
                            {t('v2.stock_import.open')}
                        </Button>
                    )}
                    {canExportPrices && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePricesExport}
                            isLoading={pricesLoading}
                            disabled={pricesLoading}
                        >
                            <PiDownloadSimpleBold className="me-1.5 size-4" />
                            {t('v2.products.export_prices')}
                        </Button>
                    )}
                    {canExportProduct && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCatalogExport}
                            isLoading={catalogLoading}
                            disabled={catalogLoading}
                        >
                            <PiDownloadSimpleBold className="me-1.5 size-4" />
                            {t('v2.products.export_catalog')}
                        </Button>
                    )}
                    {canExportProduct && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            isLoading={exportLoading}
                            disabled={exportLoading}
                        >
                            <PiDownloadSimpleBold className="me-1.5 size-4" />
                            Extrair produtos
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSyncModal(true)}
                        disabled={syncMutation.isPending}
                    >
                        <PiArrowsClockwiseBold className="me-1.5 size-4" />
                        Sincronizar Ploomes
                    </Button>
                </div>
            </PageHeader>

            {stats && (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <StatCard icon={PiCubeBold} label={t('v2.products.stat_total')} value={String(stats.total)} tone="sky" />
                    <StatCard icon={PiStackBold} label={t('v2.products.stat_in_stock')} value={String(stats.in_stock)} tone="emerald" />
                    <StatCard icon={PiTruckBold} label={t('v2.products.stat_in_transit')} value={String(stats.in_transit)} tone="amber" />
                    <StatCard icon={PiCubeBold} label={t('v2.products.stat_out_of_stock')} value={String(stats.out_of_stock)} tone="rose" />
                </div>
            )}

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <div className="flex min-h-[32rem] flex-col lg:flex-row">
                    <div className="w-full shrink-0 lg:w-64 xl:w-72">
                        <ProductCatalogSidebar active={filter} onChange={setFilter} options={filterOpts} />
                    </div>

                    <div className="min-w-0 flex-1">
                        {/* Toolbar */}
                        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700/60">
                            <div className="relative min-w-[12rem] flex-1">
                                <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                <Input
                                    type="search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar por nome, SKU ou código…"
                                    className="ps-9"
                                />
                            </div>

                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value as SortMode)}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                            >
                                <option value="name">Nome A–Z</option>
                                <option value="code">SKU</option>
                                <option value="unit_price">Preço</option>
                                <option value="stock_quantity">Estoque</option>
                            </select>

                            <div className="flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
                                <ViewToggle active={view === 'grid'} onClick={() => setView('grid')} icon={PiGridFourBold} label="Grade" />
                                <ViewToggle active={view === 'list'} onClick={() => setView('list')} icon={PiListBold} label="Lista" />
                            </div>
                        </div>

                        {/* Contador */}
                        <div className="border-b border-gray-100 px-4 py-2 dark:border-gray-700/50">
                            <Text className="text-xs text-gray-500 dark:text-gray-400">
                                {isLoading ? (
                                    <Skeleton className="inline-block h-3 w-40 align-middle" />
                                ) : (
                                    <>
                                        {`${meta?.total ?? products.length} produto(s) encontrado(s)`}
                                        {isFetching && !isLoading ? ' · atualizando…' : ''}
                                    </>
                                )}
                            </Text>
                        </div>

                        {isLoading ? (
                            view === 'grid' ? <ProductGridSkeleton /> : <ProductListSkeleton />
                        ) : products.length === 0 ? (
                            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                                <PiCubeBold className="size-12 text-gray-300 dark:text-gray-600" />
                                <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">Nenhum produto encontrado</p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">Tente outro filtro ou termo de busca.</p>
                            </div>
                        ) : view === 'grid' ? (
                            <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                                {products.map((product) => (
                                    <ProductGridCard key={product.id} product={product} />
                                ))}
                            </div>
                        ) : (
                            <div>
                                <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700/50 dark:bg-gray-800/40 dark:text-gray-400 lg:grid lg:grid-cols-[3rem_minmax(0,1.4fr)_7rem_6rem_5.5rem_5.5rem_5.5rem] lg:gap-3">
                                    <span />
                                    <span>{t('v2.products.col_product')}</span>
                                    <span>{t('v2.products.col_color')}</span>
                                    <span>{t('v2.products.col_category')}</span>
                                    <span className="text-right">{t('v2.products.col_price')}</span>
                                    <span className="text-center">{t('v2.products.col_stock_cd')}</span>
                                    <span className="text-center">{t('v2.products.col_stock_transit')}</span>
                                </div>
                                {products.map((product) => (
                                    <ProductListRow key={product.id} product={product} />
                                ))}
                            </div>
                        )}

                        {meta && (
                            <TablePagination
                                meta={meta}
                                onPageChange={setPage}
                                onPerPageChange={(value) => {
                                    setPerPage(value);
                                    setPage(1);
                                }}
                                perPageOptions={[10, 25, 48, 50, 100]}
                                itemLabel="produto"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCard({
    icon: Icon,
    label,
    value,
    tone,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string;
    tone: 'sky' | 'emerald' | 'amber' | 'rose';
}) {
    const tones = {
        sky:     'bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
        emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
        amber:   'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
        rose:    'bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className={cn('mb-3 inline-flex size-10 items-center justify-center rounded-xl', tones[tone])}>
                <Icon className="size-5" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        </div>
    );
}

function ViewToggle({
    active,
    onClick,
    icon: Icon,
    label,
}: {
    active: boolean;
    onClick: () => void;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
}) {
    return (
        <button
            type="button"
            title={label}
            onClick={onClick}
            className={cn(
                'rounded-md p-2 transition',
                active
                    ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
            )}
        >
            <Icon className="size-4" />
        </button>
    );
}
